import { createTheme } from '@mui/material/styles';

const muiTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#F0B90B',
      light: '#FFD54F',
      dark: '#C89800',
      contrastText: '#000000',
    },
    secondary: {
      main: '#F0B90B',
      light: '#FFD54F',
      dark: '#C89800',
      contrastText: '#000000',
    },
    background: {
      default: '#0F1115',
      paper: '#141821',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(240, 185, 11, 0.12)',
    action: {
      active: '#F0B90B',
      hover: 'rgba(240, 185, 11, 0.08)',
      selected: 'rgba(240, 185, 11, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
    },
    success: {
      main: '#4CAF50',
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
  },
  typography: {
    fontFamily: '"Inter", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: '0.01071em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.2)',
    '0px 4px 8px rgba(0, 0, 0, 0.3)',
    '0px 8px 16px rgba(0, 0, 0, 0.4)',
    '0px 12px 24px rgba(0, 0, 0, 0.5)',
    '0px 16px 32px rgba(0, 0, 0, 0.6)',
    '0px 2px 4px rgba(240, 185, 11, 0.1)',
    '0px 4px 8px rgba(240, 185, 11, 0.15)',
    '0px 8px 16px rgba(240, 185, 11, 0.2)',
    '0px 12px 24px rgba(240, 185, 11, 0.25)',
    '0px 16px 32px rgba(240, 185, 11, 0.3)',
    '0px 20px 40px rgba(240, 185, 11, 0.35)',
    '0px 24px 48px rgba(240, 185, 11, 0.4)',
    '0px 28px 56px rgba(240, 185, 11, 0.45)',
    '0px 32px 64px rgba(240, 185, 11, 0.5)',
    '0px 36px 72px rgba(240, 185, 11, 0.55)',
    '0px 40px 80px rgba(240, 185, 11, 0.6)',
    '0px 44px 88px rgba(240, 185, 11, 0.65)',
    '0px 48px 96px rgba(240, 185, 11, 0.7)',
    '0px 52px 104px rgba(240, 185, 11, 0.75)',
    '0px 56px 112px rgba(240, 185, 11, 0.8)',
    '0px 60px 120px rgba(240, 185, 11, 0.85)',
    '0px 64px 128px rgba(240, 185, 11, 0.9)',
    '0px 68px 136px rgba(240, 185, 11, 0.95)',
    '0px 72px 144px rgba(240, 185, 11, 1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(240, 185, 11, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #F0B90B 0%, #FFD54F 100%)',
          color: '#000000',
          '&:hover': {
            background: 'linear-gradient(135deg, #FFD54F 0%, #F0B90B 100%)',
          },
        },
        outlined: {
          borderColor: '#F0B90B',
          color: '#F0B90B',
          '&:hover': {
            borderColor: '#FFD54F',
            backgroundColor: 'rgba(240, 185, 11, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141821',
          borderRadius: 16,
          border: '1px solid rgba(240, 185, 11, 0.1)',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: 'rgba(240, 185, 11, 0.3)',
            boxShadow: '0px 8px 24px rgba(240, 185, 11, 0.2)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141821',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: 'rgba(20, 24, 33, 0.5)',
            '& fieldset': {
              borderColor: 'rgba(240, 185, 11, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(240, 185, 11, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F0B90B',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(240, 185, 11, 0.15)',
          color: '#F0B90B',
          border: '1px solid rgba(240, 185, 11, 0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141821',
          borderBottom: '1px solid rgba(240, 185, 11, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141821',
          borderRight: '1px solid rgba(240, 185, 11, 0.1)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(240, 185, 11, 0.12)',
        },
      },
    },
  },
});

export default muiTheme;
